<template>
  <div class="card" style="min-height: calc(100vh - 180px)">
    <div class="bg-blue-light p-1">
      <TitleButton btnTitle="Add Order" :showBtn="false" :showAddNew="false" :title="'Production Target vs Actual'" />
    </div>

    <div class="px-2 pt-2 section-tabs">
      <ul class="nav nav-tabs mb-0">
        <li v-for="tab in productTypeTabs" :key="tab.value" class="nav-item">
          <a
            :class="['nav-link', 'cursor-pointer', { active: activeProductType === tab.value }]"
            href="javascript:void(0)"
            @click="onChangeProductType(tab.value)"
          >
            {{ tab.label }}
          </a>
        </li>
      </ul>
    </div>

    <div class="px-2 pt-2 d-flex flex-wrap align-items-end gap-2">
      <div class="btn-group" role="group">
        <button
          v-for="option in groupByOptions"
          :key="option.value"
          type="button"
          class="btn btn-sm"
          :class="activeGroupBy === option.value ? 'btn-primary' : 'btn-outline-primary'"
          @click="onChangeGroupBy(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <button
        type="button"
        class="btn btn-outline-secondary btn-sm waves-effect ms-auto"
        :disabled="printing"
        @click="onClickPrint"
      >
        <i class="fas fa-print me-25"></i> Print
      </button>
    </div>

    <div class="px-2 pt-2">
      <DateQuerySetter btnTitle="Go" @onClickGo="onCustomDateGo" />
    </div>

    <div class="col-12 px-2 mt-2">
      <ProductionTargetActualTable :rows="rows" :group-by="activeGroupBy" />
    </div>

    <div class="px-2">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import TitleButton from '@/components/atom/TitleButton'
import Pagination from '@/components/atom/Pagination'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import ProductionTargetActualTable from '@/components/molecule/company/dashboard/dynamic-dashboard/production-target-actual-table/ProductionTargetActualTable'
import handleInventory from '@/services/modules/inventory'
import handleCompany from '@/services/modules/company'
import productionTargetActualPdf from '@/services/utils/pdf/productionTargetActualPdf'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { fetchProductionTargetActual, fetchHome } = handleInventory()
const { fetchCompanyInfo } = handleCompany()
const { exportToPDF } = productionTargetActualPdf()

const companyId = route.params.companyId
const offset = ref(20)
const rows = ref([])
const printing = ref(false)
const companyInfo = ref({})

const productTypeTabs = ref([])
const groupByOptions = ref([])

const activeProductType = ref(route.query.product_type || 'finished_goods')
const activeGroupBy = ref(route.query.group_by || 'category')

const toOptionList = (map) => {
  return Object.keys(map || {}).map(function (value) {
    return { value: value, label: map[value] }
  })
}

const loadDropdownOptions = () => {
  fetchHome().then((res) => {
    if (! res.status) return

    productTypeTabs.value = toOptionList(res.data.order_production_type)
    groupByOptions.value = toOptionList(res.data.order_production_group_by)
  })
}

const replaceQuery = async (changes) => {
  const query = Object.assign({}, route.query, changes)
  const hadPage = ! ! route.query.page
  delete query.page
  await router.replace({ path: route.path, query: query })

  if (hadPage) return

  fetchList()
}

const onChangeProductType = (value) => {
  activeProductType.value = value
  replaceQuery({ product_type: value })
}

const onChangeGroupBy = (value) => {
  activeGroupBy.value = value
  replaceQuery({ group_by: value })
}

const onCustomDateGo = () => {
  fetchList()
}

const buildQuery = () => {
  let query = '?company_id=' + companyId
  query += '&product_type=' + activeProductType.value
  query += '&group_by=' + activeGroupBy.value
  query += '&offset=' + offset.value
  query += '&start_date=' + route.query.start
  query += '&end_date=' + route.query.end

  if (route.query.page) {
    query += '&page=' + route.query.page
  }

  return query
}

const setPagination = (data) => {
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page
  })
}

const fetchList = () => {
  fetchProductionTargetActual(buildQuery()).then((res) => {
    if (! res.status) {
      rows.value = []
      store.commit('resetPagination')
      return
    }

    rows.value = res.data.items
    setPagination(res.data)
  })
}

const loadCompanyInfo = async () => {
  if (companyInfo.value.id) return
  await fetchCompanyInfo(companyId).then((res) => { companyInfo.value = res.data })
}

const printFilters = () => {
  const activeTab = productTypeTabs.value.find((tab) => tab.value === activeProductType.value)
  const productTypeLabel = activeTab ? activeTab.label : activeProductType.value

  return {
    productTypeLabel: productTypeLabel,
    groupBy: activeGroupBy.value,
    start: route.query.start,
    end: route.query.end
  }
}

const onClickPrint = async () => {
  if (printing.value) return

  printing.value = true
  await loadCompanyInfo()
  const user = store.state.user.user

  await fetchProductionTargetActual(buildQuery() + '&is_all=1').then((res) => {
    if (! res.status) {
      printing.value = false
      return
    }

    exportToPDF(companyInfo.value, res.data.items, printFilters(), user.name)
    printing.value = false
  }).catch(() => {
    printing.value = false
  })
}

const onPageChange = (page) => {
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  router.push({ path: route.path, query: routeQuery })
}

const buildDefaultQuery = () => {
  const defaults = {}

  if (! route.query.start || ! route.query.end) {
    const now = new Date()
    defaults.start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
    defaults.end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)
  }

  if (! route.query.product_type) {
    defaults.product_type = activeProductType.value
  }

  if (! route.query.group_by) {
    defaults.group_by = activeGroupBy.value
  }

  return defaults
}

const applyDefaultFilters = async () => {
  const defaults = buildDefaultQuery()

  if (Object.keys(defaults).length === 0) return

  await router.replace({ path: route.path, query: Object.assign({}, route.query, defaults) })
}

onMounted(async () => {
  loadDropdownOptions()
  await applyDefaultFilters()
  fetchList()
})

watch(() => route.query.page, () => {
  fetchList()
})
</script>
